import chatTemplate from '@templates/website/partials/chatChannel.html.njk';
import parseStringAsHtml from '@naturehouse/nh-essentials/lib/html/parser';
import HttpClient from '../../../common/HttpClient';
import type { TranslationType } from '../../../common/TranslationManager';
import TranslationManager from '../../../common/TranslationManager';
import '../../../../css/components/_chatChannelList.pcss';

enum ChatActions {
    ARCHIVE = 'archive',
    REOPEN = 'reopen'
}

export default class ChatChannelListComponent {
    readonly #context: ContextInterface;

    readonly #httpClient: HttpClient = HttpClient;

    readonly #listElement: HTMLElement | null = null;

    constructor(context: ContextInterface) {
        this.#context = context;
        this.#context.bindings.channels = [];

        this.#listElement = this.#context.element.querySelector('[data-role="chat-list"]');
    }

    public onInit(): void {
        this.#getChannels();
    }

    async #getTranslations(): Promise<TranslationType> {
        const translationManager = await TranslationManager.getInstance();
        return translationManager.translations;
    }

    #setEventListeners(): void {
        const archiveButtons: HTMLButtonElement[] = Array.from(
            this.#context.element.querySelectorAll('[data-role="archive"]')
        );

        archiveButtons.forEach((b: HTMLButtonElement) => {
            const button: HTMLButtonElement = b;
            button.addEventListener('click', this.#archiveChat.bind(this));
        });
    }

    #archiveChat(event: Event): void {
        const button = event.currentTarget as HTMLButtonElement;
        const chatElement: HTMLElement | null = button.closest('[data-endpoint]');
        if (!chatElement) {
            return;
        }

        const endpoint: string | undefined = chatElement.dataset.endpoint;
        if (!endpoint) {
            return;
        }

        const url: URL = new URL(endpoint);
        this.#httpClient.post(url.href).then(() => {
            const listElement: HTMLElement | null = chatElement.parentElement;
            if (!listElement) {
                return;
            }

            listElement.remove();
        });
    }

    #getActionTranslationKey(): string {
        if (this.#context.bindings.action === ChatActions.ARCHIVE) {
            return 'archive.label';
        }

        if (this.#context.bindings.action === ChatActions.REOPEN) {
            return 'reopen.label';
        }

        throw new Error(`Chat action ${this.#context.bindings.action} is not supported`);
    }

    #getChannels(): void {
        if (!this.#context.bindings.endpoint) {
            return;
        }

        this.#context.element.classList.add('hide-content');

        this.#sleep(150).then(() => {
            this.#httpClient
                .get(this.#context.bindings.endpoint)
                .then(async (response) => {
                    const channelAmountElement: HTMLElement | null =
                        this.#context.element.querySelector('[data-role="channel-amount"]');

                    if (response && response.channels.length > 0) {
                        const maxLength = 80;
                        const translations = await this.#getTranslations();

                        const channels = response.channels.map((channel: ChatChannelResponse) => {
                            channel.message =
                                channel.message.length > maxLength
                                    ? `${channel.message.substr(0, maxLength)} ...`
                                    : channel.message;

                            channel.action = translations[this.#getActionTranslationKey()];
                            channel.reply = translations['respond.label'];

                            if (this.#context.bindings.user === 'landlord') {
                                channel.channel_path = channel.landlord_channel_path;
                            }

                            if (this.#context.bindings.action === 'archive') {
                                channel.action_path = channel.archive_path;
                            }

                            if (this.#context.bindings.action === 'reopen') {
                                channel.action_path = channel.reopen_path;
                            }

                            return channel;
                        });

                        this.#renderChatChannels(channels).then(() => {
                            this.#setEventListeners();
                        });

                        if (channelAmountElement) {
                            channelAmountElement.innerText = `${response.channels.length}`;
                        }

                        this.#setEventListeners();
                    } else if (channelAmountElement) {
                        channelAmountElement.innerText = '0';
                    }
                })
                .finally((): void => {
                    this.#context.element.classList.remove('hide-content');
                    this.#context.element.classList.remove('loading-spinner');
                });
        });
    }

    async #renderChatChannels(channels: ChatChannel[]): Promise<void> {
        channels.forEach((channel: ChatChannel) => {
            chatTemplate.render(channel, (err, res) => {
                if (err instanceof Error) {
                    throw err;
                }

                if (!res) {
                    return;
                }

                if (!this.#listElement) {
                    throw new Error('No list element defined');
                }

                const html = parseStringAsHtml(res, 'aside');
                const listItem = document.createElement('li');
                listItem.classList.add('chat-channel');
                listItem.appendChild(html);
                this.#listElement.appendChild(listItem);
            });
        });
    }

    #sleep(time: number): Promise<any> {
        return new Promise((resolve) => {
            setTimeout(resolve, time);
        });
    }

    static getClassName(): string {
        return 'ChatChannelListComponent';
    }
}
